import { Box, styled } from '@mui/material'
import type { OnwardQuoteSuggestion } from 'hooks/useOnwardBookings'
import { OnwardSuggestionCard } from './OnwardBookingCards'

export const BUBBLE_WIDTH = 360
export const BUBBLE_TAIL_HEIGHT = 12
const TAIL_HALF_WIDTH = 10
const BUBBLE_BG = 'rgba(255, 255, 255, 0.97)'
const BUBBLE_BORDER = 'rgba(10, 37, 61, 0.12)'

export type BubbleTailSide = 'top' | 'right' | 'bottom' | 'left'

const clampAttach = (attach: number) => Math.max(0.08, Math.min(0.92, attach))

const anchorTransform = (side: BubbleTailSide, attach: number): string => {
  const a = clampAttach(attach)
  switch (side) {
    case 'bottom':
      return `translate(${-a * 100}%, calc(-100% - ${BUBBLE_TAIL_HEIGHT}px))`
    case 'top':
      return `translate(${-a * 100}%, ${BUBBLE_TAIL_HEIGHT}px)`
    case 'left':
      return `translate(${BUBBLE_TAIL_HEIGHT}px, ${-a * 100}%)`
    case 'right':
      return `translate(calc(-100% - ${BUBBLE_TAIL_HEIGHT}px), ${-a * 100}%)`
  }
}

const BubbleAnchor = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tailSide' && prop !== 'tailAttach',
})<{ tailSide: BubbleTailSide; tailAttach: number }>(
  ({ tailSide, tailAttach }) => ({
    position: 'relative',
    transform: anchorTransform(tailSide, tailAttach),
  }),
)

const BubbleRoot = styled(Box)({
  position: 'relative',
  width: BUBBLE_WIDTH,
  filter: 'drop-shadow(0 8px 24px rgba(10, 37, 61, 0.22))',
})

const BubbleBody = styled(Box)({
  borderRadius: 12,
  background: BUBBLE_BG,
  border: `1px solid ${BUBBLE_BORDER}`,
  padding: '10px 10px 4px 10px',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
})

const tailStyle = (
  side: BubbleTailSide,
  attach: number,
): Record<string, string | number> => {
  const a = clampAttach(attach)
  switch (side) {
    case 'bottom':
      return {
        bottom: -BUBBLE_TAIL_HEIGHT + 1,
        left: `calc(${a * 100}% - ${TAIL_HALF_WIDTH}px)`,
        width: 0,
        height: 0,
        borderLeft: `${TAIL_HALF_WIDTH}px solid transparent`,
        borderRight: `${TAIL_HALF_WIDTH}px solid transparent`,
        borderTop: `${BUBBLE_TAIL_HEIGHT}px solid ${BUBBLE_BG}`,
      }
    case 'top':
      return {
        top: -BUBBLE_TAIL_HEIGHT + 1,
        left: `calc(${a * 100}% - ${TAIL_HALF_WIDTH}px)`,
        width: 0,
        height: 0,
        borderLeft: `${TAIL_HALF_WIDTH}px solid transparent`,
        borderRight: `${TAIL_HALF_WIDTH}px solid transparent`,
        borderBottom: `${BUBBLE_TAIL_HEIGHT}px solid ${BUBBLE_BG}`,
      }
    case 'right':
      return {
        right: -BUBBLE_TAIL_HEIGHT + 1,
        top: `calc(${a * 100}% - ${TAIL_HALF_WIDTH}px)`,
        width: 0,
        height: 0,
        borderTop: `${TAIL_HALF_WIDTH}px solid transparent`,
        borderBottom: `${TAIL_HALF_WIDTH}px solid transparent`,
        borderLeft: `${BUBBLE_TAIL_HEIGHT}px solid ${BUBBLE_BG}`,
      }
    case 'left':
      return {
        left: -BUBBLE_TAIL_HEIGHT + 1,
        top: `calc(${a * 100}% - ${TAIL_HALF_WIDTH}px)`,
        width: 0,
        height: 0,
        borderTop: `${TAIL_HALF_WIDTH}px solid transparent`,
        borderBottom: `${TAIL_HALF_WIDTH}px solid transparent`,
        borderRight: `${BUBBLE_TAIL_HEIGHT}px solid ${BUBBLE_BG}`,
      }
  }
}

const BubbleTail = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tailSide' && prop !== 'tailAttach',
})<{ tailSide: BubbleTailSide; tailAttach: number }>(
  ({ tailSide, tailAttach }) => ({
    position: 'absolute',
    pointerEvents: 'none',
    ...tailStyle(tailSide, tailAttach),
  }),
)

interface OnwardSuggestionBubbleProps {
  suggestion: OnwardQuoteSuggestion
  onSelect: (suggestion: OnwardQuoteSuggestion) => void
  tailSide?: BubbleTailSide
  tailAttach?: number
}

const OnwardSuggestionBubble: React.FC<OnwardSuggestionBubbleProps> = ({
  suggestion,
  onSelect,
  tailSide = 'bottom',
  tailAttach = 0.5,
}) => (
  <BubbleAnchor tailSide={tailSide} tailAttach={tailAttach}>
    <BubbleRoot>
      <BubbleBody>
        <OnwardSuggestionCard suggestion={suggestion} onSelect={onSelect} />
      </BubbleBody>
      <BubbleTail tailSide={tailSide} tailAttach={tailAttach} />
    </BubbleRoot>
  </BubbleAnchor>
)

export default OnwardSuggestionBubble
